import ItiDataGrid from "@/iti-core-react/components/ItiDataGrid/ItiDataGrid";
import { AuthContext } from "@/iti-core-react/contexts/auth";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { ItiMessageService } from "@/iti-core-react/services/iti-message-service";
import { DatasetSearchResultDtoV2 } from "@/shared/dtos/Governance/dataset-search-result-dto-v2";
import { DatasetFilterDtoV2StatusEnum } from "@/shared/enums/Governance/dataset-filter-dto-v2-status-enum";
import { UserRole } from "@/shared/enums/user-roles";
import { RecycleDatasetService } from "@/shared/services/recycleDatasetService";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { getDatasetColumns } from "../DatasetColumns";
import DatasetDetailsPopUp from "../DatasetDetailsPopUp/DatasetDetailsPopUp";
import { useDatasetSearch } from "../useDataset";

export default function RecycleDatasetList() {
	const { t } = useLanguage();
	const { profile } = useContext(AuthContext);
	const [showDetailsPopup, setShowDetailsPopup] = useState<boolean>(false);
	const [datasetDetails, setDatasetDetails] =
		useState<DatasetSearchResultDtoV2 | null>(null);

	const gridRef = useRef<HTMLDivElement>(null);

	const {
		store: datasetStore,
		pageIndex,
		pageSize,
		handlePageIndexChanged,
		handlePageSizeChanged,
	} = useDatasetSearch(0, 10, DatasetFilterDtoV2StatusEnum.DELETED);

	const baseColumns = useMemo(() => getDatasetColumns(t), [t]);

	const canPermanentDelete: boolean =
		profile?.roleName === UserRole.ADMIN_GROUP ||
		profile?.roleName === UserRole.ADMIN_ORG;

	const onViewDetails = useCallback((e: any) => {
		const dataset = e.row.data as DatasetSearchResultDtoV2;
		setDatasetDetails(dataset);
		setShowDetailsPopup(true);
	}, []);

	const onRestoreDataset = useCallback(
		async (e: any) => {
			const dataset = e.row.data as DatasetSearchResultDtoV2;
			try {
				if (dataset.id) {
					const success = await RecycleDatasetService.restoreDataset(
						dataset.id,
					);
					if (success) {
						ItiMessageService.toastSuccess(
							t("recycle.dataset-restored-successfully", {
								name: dataset.title,
							}),
						);
						datasetStore.load();
					}
				}
			} catch (error) {
				ItiMessageService.toastError(t("recycle.error-restoring-dataset"));
			}
		},
		[t, datasetStore],
	);

	const onPermanentDelete = useCallback(
		async (e: any) => {
			const dataset = e.row.data as DatasetSearchResultDtoV2;
			ItiMessageService.confirmYesNoButtons(
				t("recycle.confirm-permanent-delete", {
					name: dataset.title,
				}),
			).then(async (confirmed: boolean) => {
				if (confirmed && dataset.id) {
					try {
						const success = await RecycleDatasetService.permanentDeleteDataset(
							dataset.id,
						);
						if (success) {
							ItiMessageService.toastSuccess(
								t("recycle.dataset-deleted-permanently", {
									name: dataset.title,
								}),
							);
							datasetStore.load();
						}
					} catch (error) {
						ItiMessageService.toastError(
							t("recycle.error-deleting-dataset-permanently"),
						);
					}
				}
			});
		},
		[t, datasetStore],
	);

	const rowButtons = useMemo(
		() => [
			{
				hint: t("recycle.view-details"),
				icon: "mdi mdi-eye",
				name: "view-details",
				visible: true,
				onClick: onViewDetails,
			},
			{
				hint: t("recycle.restore"),
				icon: "mdi mdi-delete-restore",
				name: "restore",
				visible: true,
				onClick: onRestoreDataset,
			},
			{
				hint: t("recycle.permanent-delete"),
				icon: "mdi mdi-delete-forever",
				name: "permanent-delete",
				visible: canPermanentDelete,
				onClick: onPermanentDelete,
			},
		],
		[t, canPermanentDelete, onViewDetails, onRestoreDataset, onPermanentDelete],
	);

	const datasetColumns: any = useMemo(
		() => [
			...baseColumns,
			{
				caption: t("actions"),
				type: "buttons",
				buttons: rowButtons,
				fixed: true,
				fixedPosition: "right",
			},
		],
		[baseColumns, rowButtons, t],
	);

	return (
		<>
			<div ref={gridRef} className="position-relative">
				<ItiDataGrid
					keyExpr="id"
					entity={"deletedDatasets"}
					elementAttr={{ class: "basic-grid-toolbar-white" }}
					dataSource={datasetStore}
					columns={datasetColumns}
					rowButtons={rowButtons}
					allowDeleting={false}
					allowUpdating={false}
					allowAdding={false}
					pagingEnabled={true}
					pagerVisible={true}
					remoteOperations={true}
					showPageSizeSelector={true}
					pageSize={pageSize}
					pageIndex={pageIndex}
					onPageIndexChanged={handlePageIndexChanged}
					onPageSizeChanged={handlePageSizeChanged}
					filterRowVisible={true}
					sortingMode="multiple"
					allowColumnResizing={false}
				/>
			</div>

			{/* Popup de detalles */}
			{showDetailsPopup && datasetDetails && (
				<DatasetDetailsPopUp
					dataset={datasetDetails}
					onHide={() => {
						setShowDetailsPopup(false);
						setDatasetDetails(null);
					}}
				/>
			)}
		</>
	);
}
